import { useEffect, useState } from 'react'
import { useBudgetMonth, useFundAllUnderfunded, useSeedStarterCategories } from './hooks'
import { useCreateNextMonth, useRolloverCatchUp } from './rollover-hooks'
import { currentYearMonth, formatCurrency, formatMonthLabel, stepMonth } from './format'
import { totalUnderfundedByGoal } from './calc'
import { MonthPicker } from './MonthPicker'
import { ToBeAssignedHeader } from './ToBeAssignedHeader'
import { BudgetTable, type BudgetFilter } from './BudgetTable'
import { MoveMoneyDialog } from './MoveMoneyDialog'

const LS_KEY = 'crawfish.budget.activeMonth'

function loadStoredMonth(): { year: number; month: number } | null {
  try {
    const raw = localStorage.getItem(LS_KEY)
    if (!raw) return null
    const parsed = JSON.parse(raw) as { year?: unknown; month?: unknown }
    if (
      typeof parsed.year === 'number' &&
      typeof parsed.month === 'number' &&
      parsed.month >= 1 &&
      parsed.month <= 12
    ) {
      return { year: parsed.year, month: parsed.month }
    }
  } catch {
    // Corrupt LS state is fine — fall back to today.
  }
  return null
}

/**
 * Top-level container for /budget. Owns the active (year, month) and
 * orchestrates loading, empty-state seeding, and the building blocks
 * (picker / header / table / move-money dialog).
 */
export function BudgetScreen() {
  const [active, setActive] = useState(() => loadStoredMonth() ?? currentYearMonth())
  const [moveOpen, setMoveOpen] = useState(false)
  const [filter, setFilter] = useState<BudgetFilter>('all')
  const monthQuery = useBudgetMonth(active.year, active.month)
  const prev = stepMonth(active.year, active.month, -1)
  const prevQuery = useBudgetMonth(prev.year, prev.month)
  const seed = useSeedStarterCategories()
  const fundAll = useFundAllUnderfunded()
  const createNext = useCreateNextMonth()
  useRolloverCatchUp()

  useEffect(() => {
    try {
      localStorage.setItem(LS_KEY, JSON.stringify(active))
    } catch {
      // localStorage failures (private mode quotas, etc.) are
      // non-fatal — the user just won't get persistence across
      // page reloads.
    }
  }, [active])

  return (
    <div className="mx-auto max-w-5xl space-y-6">
      <header className="flex items-center justify-between">
        <h1 className="text-2xl font-semibold">Budget</h1>
        <MonthPicker
          year={active.year}
          month={active.month}
          onChange={(y, m) => setActive({ year: y, month: m })}
        />
      </header>

      {monthQuery.isLoading && (
        <div className="rounded-xl border border-zinc-200 p-6 text-sm text-zinc-500 dark:border-zinc-800">
          Loading…
        </div>
      )}

      {monthQuery.isError && (
        <div
          role="alert"
          className="rounded-xl border border-overspent bg-overspent-subtle p-4 text-sm text-overspent"
        >
          Couldn't load this month's budget. {String(monthQuery.error)}
        </div>
      )}

      {monthQuery.isSuccess &&
        !monthQuery.data &&
        (prevQuery.data && prevQuery.data.categoryGroups.length > 0 ? (
          <RolloverCTA
            targetLabel={formatMonthLabel(active.year, active.month)}
            priorLabel={formatMonthLabel(prev.year, prev.month)}
            busy={createNext.isPending}
            onRoll={() =>
              createNext.mutate({
                from: prevQuery.data!,
                year: active.year,
                month: active.month,
              })
            }
          />
        ) : (
          <EmptyState
            onSeed={() => seed.mutate({ year: active.year, month: active.month })}
            busy={seed.isPending}
          />
        ))}

      {monthQuery.isSuccess && monthQuery.data && monthQuery.data.categoryGroups.length === 0 && (
        <EmptyState
          onSeed={() => seed.mutate({ year: active.year, month: active.month })}
          busy={seed.isPending}
        />
      )}

      {monthQuery.isSuccess && monthQuery.data && monthQuery.data.categoryGroups.length > 0 && (
        <>
          <ToBeAssignedHeader month={monthQuery.data} />
          <div className="flex flex-wrap items-center justify-end gap-2">
            {(() => {
              const owed = totalUnderfundedByGoal(monthQuery.data)
              if (owed <= 0) return null
              return (
                <button
                  type="button"
                  onClick={() => fundAll.mutate({ year: active.year, month: active.month })}
                  disabled={fundAll.isPending}
                  className="rounded-md bg-blue-600 px-3 py-1 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50"
                  title="Top up every goal-tracked category to its monthly target"
                >
                  {fundAll.isPending ? 'Funding…' : `Fund all underfunded (${formatCurrency(owed)})`}
                </button>
              )
            })()}
            <button
              type="button"
              onClick={() => setMoveOpen(true)}
              className="rounded-md border border-zinc-300 px-3 py-1 text-sm hover:bg-zinc-100 dark:border-zinc-700 dark:hover:bg-zinc-800"
            >
              Move money
            </button>
          </div>
          <FilterChips filter={filter} onChange={setFilter} />
          <BudgetTable month={monthQuery.data} year={active.year} monthNumber={active.month} filter={filter} />
          <MoveMoneyDialog
            month={monthQuery.data}
            year={active.year}
            monthNumber={active.month}
            open={moveOpen}
            onClose={() => setMoveOpen(false)}
          />
        </>
      )}
    </div>
  )
}

const FILTERS: Array<{ id: BudgetFilter; label: string }> = [
  { id: 'all', label: 'All' },
  { id: 'underfunded', label: 'Underfunded' },
  { id: 'overfunded', label: 'Overfunded' },
  { id: 'available', label: 'Money available' },
  { id: 'goal', label: 'With goal' },
]

function FilterChips({
  filter,
  onChange,
}: {
  filter: BudgetFilter
  onChange: (f: BudgetFilter) => void
}) {
  return (
    <div role="tablist" className="flex flex-wrap gap-1 text-xs">
      {FILTERS.map((f) => {
        const active = filter === f.id
        return (
          <button
            key={f.id}
            type="button"
            role="tab"
            aria-selected={active}
            onClick={() => onChange(f.id)}
            className={`rounded-full px-3 py-1 ${
              active
                ? 'bg-blue-600 text-white'
                : 'border border-zinc-300 text-zinc-700 hover:bg-zinc-100 dark:border-zinc-700 dark:text-zinc-300 dark:hover:bg-zinc-800'
            }`}
          >
            {f.label}
          </button>
        )
      })}
    </div>
  )
}

function RolloverCTA({
  targetLabel,
  priorLabel,
  busy,
  onRoll,
}: {
  targetLabel: string
  priorLabel: string
  busy: boolean
  onRoll: () => void
}) {
  return (
    <div className="rounded-xl border-2 border-dashed border-zinc-300 bg-white p-10 text-center dark:border-zinc-700 dark:bg-zinc-900">
      <h2 className="text-lg font-semibold">{targetLabel} isn't set up yet</h2>
      <p className="mt-2 text-sm text-zinc-500 dark:text-zinc-400">
        Roll over from {priorLabel} — categories, goals, and leftover available balances carry
        forward. Overspending resets and reduces To Be Assigned.
      </p>
      <div className="mt-6 flex justify-center">
        <button
          type="button"
          onClick={onRoll}
          disabled={busy}
          className="rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50"
        >
          {busy ? 'Creating…' : `Start ${targetLabel}`}
        </button>
      </div>
    </div>
  )
}

function EmptyState({ onSeed, busy }: { onSeed: () => void; busy: boolean }) {
  return (
    <div className="rounded-xl border-2 border-dashed border-zinc-300 bg-white p-10 text-center dark:border-zinc-700 dark:bg-zinc-900">
      <h2 className="text-lg font-semibold">No categories yet</h2>
      <p className="mt-2 text-sm text-zinc-500 dark:text-zinc-400">
        Start with a YNAB-style template, or add your own categories one by one.
      </p>
      <div className="mt-6 flex justify-center gap-3">
        <button
          type="button"
          onClick={onSeed}
          disabled={busy}
          className="rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50"
        >
          {busy ? 'Adding…' : 'Use starter categories'}
        </button>
      </div>
    </div>
  )
}
